import { db } from '../config/firebase';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  Timestamp,
  writeBatch,
  DocumentSnapshot,
  QueryConstraint,
  increment,
} from 'firebase/firestore';
import {
  Workload,
  WorkloadStatus,
  WorkloadType,
  CreateWorkloadDto,
  UpdateWorkloadDto,
  WorkloadFilters,
  WorkloadProgress,
} from '../types/workload.types';
import { assignmentService } from './assignment.service';

const COLLECTION_NAME = 'workloads';

// Helper to remove undefined values (Firebase doesn't accept undefined)
function removeUndefined<T extends Record<string, any>>(obj: T): T {
  const cleaned: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined) continue;
    if (value !== null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Timestamp)) {
      cleaned[key] = removeUndefined(value);
    } else {
      cleaned[key] = value;
    }
  }
  return cleaned as T;
}


export async function createWorkload(
  data: CreateWorkloadDto,
  userId: string,
  userName: string
): Promise<Workload> {
  const now = Timestamp.now();

  // Clean territory to remove undefined values
  const cleanedTerritory = removeUndefined(data.territory);

  const workloadData = removeUndefined({
    ...data,
    territory: cleanedTerritory,
    status: 'active' as WorkloadStatus,  // Start as active so assigned users can immediately work
    progress: {
      totalAddresses: 0,
      pending: 0,
      visited: 0,
      notHome: 0,
      refused: 0,
      completed: 0,
      inaccessible: 0,
      completionPercentage: 0,
      lastActivityAt: now,
    },
    assignedTo: data.assignedTo || [],
    assignedToNames: data.assignedToNames || [],
    assignedGroups: data.assignedGroups || [],
    surveyId: data.surveyId || null,
    surveyName: data.surveyName || null,
    createdBy: userId,
    createdByName: userName,
    createdAt: now,
    updatedAt: now,
  });

  const docRef = await addDoc(collection(db, COLLECTION_NAME), workloadData);
  const workload: Workload = {
    id: docRef.id,
    ...workloadData,
  } as Workload;

  // Log creation to W-A-L
  await assignmentService.logAction({
    workloadId: workload.id,
    workloadName: workload.name,
    projectId: workload.projectId,
    action: 'created',
    description: `Workload "${workload.name}" was created`,
    changes: [],
  });

  return workload;
}

export async function createFromSaturation(
  data: CreateWorkloadDto & {
    territory: Workload['territory'];
    addresses: Array<{
      street: string;
      city: string;
      state: string;
      zipCode: string;
      location: { lat: number; lng: number };
    }>;
  },
  userId: string,
  userName: string
): Promise<Workload> {
  const workload = await createWorkload(
    {
      ...data,
      type: 'saturation',
      sourceMetadata: {
        pointLocation: data.territory.centerPoint,
        radiusMeters: data.territory.radiusMeters,
        addressCount: data.addresses.length,
        createdAt: Timestamp.now(),
      },
    },
    userId,
    userName
  );

  // Addresses will be added via address.service
  return workload;
}

export async function createFromCsv(
  data: CreateWorkloadDto & {
    csvFileName: string;
    columnMapping: Record<string, string>;
    rowCount: number;
  },
  userId: string,
  userName: string
): Promise<Workload> {
  return createWorkload(
    {
      ...data,
      type: 'csv_upload',
      sourceMetadata: {
        fileName: data.csvFileName,
        columnMapping: data.columnMapping,
        uploadedAt: Timestamp.now(),
        originalRowCount: data.rowCount,
        validRowCount: data.rowCount,
      },
    },
    userId,
    userName
  );
}

export async function createFromCensusBlocks(
  data: CreateWorkloadDto & {
    censusBlockIds: string[];
    addressCount: number;
  },
  userId: string,
  userName: string
): Promise<Workload> {
  return createWorkload(
    {
      ...data,
      type: 'census_block',
      sourceMetadata: {
        censusBlockIds: data.censusBlockIds,
        fetchedAt: Timestamp.now(),
        addressCount: data.addressCount,
      },
    },
    userId,
    userName
  );
}


export async function getWorkload(workloadId: string): Promise<Workload | null> {
  const docRef = doc(db, COLLECTION_NAME, workloadId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as Workload;
}

export async function getProjectWorkloads(
  projectId: string,
  filters?: WorkloadFilters
): Promise<Workload[]> {
  const constraints: QueryConstraint[] = [
    where('projectId', '==', projectId),
    orderBy('createdAt', 'desc'),
  ];

  if (filters?.status && filters.status.length > 0) {
    constraints.push(where('status', 'in', filters.status));
  }

  if (filters?.assignedTo && filters.assignedTo.length > 0) {
    constraints.push(where('assignedTo', 'array-contains-any', filters.assignedTo));
  }

  const q = query(collection(db, COLLECTION_NAME), ...constraints);
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Workload[];
}

export async function getUserWorkloads(userId: string): Promise<Workload[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('assignedTo', 'array-contains', userId),
    where('status', 'in', ['active', 'paused']),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Workload[];
}

export async function getWorkloadsPaginated(
  projectId: string,
  pageSize: number = 20,
  lastDoc?: DocumentSnapshot
): Promise<{ workloads: Workload[]; lastDoc: DocumentSnapshot | null }> {
  const constraints: QueryConstraint[] = [
    where('projectId', '==', projectId),
    orderBy('createdAt', 'desc'),
    limit(pageSize),
  ];

  if (lastDoc) {
    constraints.push(startAfter(lastDoc));
  }

  const q = query(collection(db, COLLECTION_NAME), ...constraints);
  const snapshot = await getDocs(q);

  const workloads = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Workload[];

  const newLastDoc = snapshot.docs[snapshot.docs.length - 1] || null;

  return { workloads, lastDoc: newLastDoc };
}


export async function updateWorkload(
  workloadId: string,
  data: UpdateWorkloadDto,
  userId: string
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, workloadId);
  const existing = await getDoc(docRef);

  if (!existing.exists()) {
    throw new Error('Workload not found');
  }

  const existingData = existing.data() as Workload;
  const changes: Array<{ field: string; previousValue: any; newValue: any }> = [];

  Object.keys(data).forEach((key) => {
    const prevValue = (existingData as any)[key];
    const newValue = (data as any)[key];
    if (JSON.stringify(prevValue) !== JSON.stringify(newValue)) {
      changes.push({
        field: key,
        previousValue: prevValue,
        newValue: newValue,
      });
    }
  });

  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now(),
  });

  if (changes.length > 0 && changes.some((c) => c.field !== 'progress')) {
    await assignmentService.logAction({
      workloadId,
      workloadName: existingData.name,
      projectId: existingData.projectId,
      action: 'status_changed',
      description: `Workload updated: ${changes.map((c) => c.field).join(', ')}`,
      changes,
    });
  }
}

export async function updateWorkloadStatus(
  workloadId: string,
  status: WorkloadStatus,
  userId: string
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, workloadId);
  const existing = await getDoc(docRef);

  if (!existing.exists()) {
    throw new Error('Workload not found');
  }

  const existingData = existing.data() as Workload;
  const previousStatus = existingData.status;

  await updateDoc(docRef, {
    status,
    updatedAt: Timestamp.now(),
  });

  await assignmentService.logAction({
    workloadId,
    workloadName: existingData.name,
    projectId: existingData.projectId,
    action: 'status_changed',
    description: `Status changed from ${previousStatus} to ${status}`,
    changes: [
      {
        field: 'status',
        previousValue: previousStatus,
        newValue: status,
        displayPrevious: previousStatus,
        displayNew: status,
      },
    ],
  });
}

export async function updateWorkloadProgress(
  workloadId: string,
  progress: Partial<WorkloadProgress>
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, workloadId);

  const updateData: Record<string, any> = {
    updatedAt: Timestamp.now(),
  };

  // Update nested progress fields
  Object.entries(progress).forEach(([key, value]) => {
    updateData[`progress.${key}`] = value;
  });

  await updateDoc(docRef, updateData);
}

/**
 * Recalculate workload progress by querying all addresses
 */
export async function recalculateProgress(workloadId: string): Promise<WorkloadProgress> {
  const addressesRef = collection(db, COLLECTION_NAME, workloadId, 'addresses');
  const snapshot = await getDocs(addressesRef);

  const progress: WorkloadProgress = {
    totalAddresses: 0,
    pending: 0,
    visited: 0,
    notHome: 0,
    refused: 0,
    completed: 0,
    discontinued: 0,
    inaccessible: 0,
    leadsGenerated: 0,
    surveyResponses: 0,
    completionPercentage: 0,
    lastActivityAt: Timestamp.now(),
  };

  snapshot.docs.forEach((doc) => {
    const data = doc.data();
    progress.totalAddresses++;

    switch (data.status) {
      case 'pending':
        progress.pending++;
        break;
      case 'visited':
        progress.visited++;
        break;
      case 'not_home':
        progress.notHome++;
        break;
      case 'refused':
        progress.refused++;
        break;
      case 'completed':
        progress.completed++;
        break;
      case 'discontinued':
        progress.discontinued++;
        break;
      case 'inaccessible':
        progress.inaccessible++;
        break;
    }

    if (data.leadId) {
      progress.leadsGenerated++;
    }

    if (data.surveyResponseId) {
      progress.surveyResponses++;
    }
  });

  // Calculate completion percentage
  if (progress.totalAddresses > 0) {
    const workingAddresses = progress.completed + progress.refused + progress.discontinued + progress.inaccessible;
    progress.completionPercentage = Math.round((workingAddresses / progress.totalAddresses) * 100);
  }

  // Update the workload document
  await updateWorkloadProgress(workloadId, progress);

  return progress;
}

/**
 * Increment survey response count and update completion status
 * Called when a survey response is created
 */
export async function incrementSurveyResponse(
  workloadId: string,
  completed: boolean = true
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, workloadId);

  const updateData: Record<string, any> = {
    'progress.surveyResponses': increment(1),
    'progress.lastActivityAt': Timestamp.now(),
    updatedAt: Timestamp.now(),
  };

  if (completed) {
    updateData['progress.completed'] = increment(1);
    updateData['progress.pending'] = increment(-1);
  }

  await updateDoc(docRef, updateData);
}

export async function linkSurvey(
  workloadId: string,
  surveyId: string,
  surveyName: string,
  userId: string
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, workloadId);
  const existing = await getDoc(docRef);

  if (!existing.exists()) {
    throw new Error('Workload not found');
  }

  const existingData = existing.data() as Workload;

  await updateDoc(docRef, {
    surveyId,
    surveyName, // Also save the survey name so it displays correctly
    updatedAt: Timestamp.now(),
  });

  await assignmentService.logAction({
    workloadId,
    workloadName: existingData.name,
    projectId: existingData.projectId,
    action: 'survey_linked',
    description: `Survey "${surveyName}" was linked`,
    changes: [
      {
        field: 'surveyId',
        previousValue: existingData.surveyId || null,
        newValue: surveyId,
      },
    ],
    metadata: { surveyName },
  });
}

export async function unlinkSurvey(
  workloadId: string,
  userId: string
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, workloadId);
  const existing = await getDoc(docRef);

  if (!existing.exists()) {
    throw new Error('Workload not found');
  }

  const existingData = existing.data() as Workload;
  const previousSurveyName = existingData.surveyName;

  await updateDoc(docRef, {
    surveyId: null,
    surveyName: null,
    updatedAt: Timestamp.now(),
  });

  await assignmentService.logAction({
    workloadId,
    workloadName: existingData.name,
    projectId: existingData.projectId,
    action: 'survey_unlinked',
    description: previousSurveyName
      ? `Survey "${previousSurveyName}" was unlinked`
      : 'Survey was unlinked',
    changes: [
      {
        field: 'surveyId',
        previousValue: existingData.surveyId || null,
        newValue: null,
      },
    ],
    metadata: { previousSurveyName },
  });
}


export async function assignToUsers(
  workloadId: string,
  userIds: string[],
  userNames: string[],
  performedBy: string,
  performedByName?: string
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, workloadId);
  const existing = await getDoc(docRef);

  if (!existing.exists()) {
    throw new Error('Workload not found');
  }

  const existingData = existing.data() as Workload;
  const previousAssigned = existingData.assignedTo || [];

  // Merge with existing assignments
  const newAssignedTo = [...new Set([...previousAssigned, ...userIds])];
  const newAssignedNames = [...new Set([...(existingData.assignedToNames || []), ...userNames])];

  await updateDoc(docRef, {
    assignedTo: newAssignedTo,
    assignedToNames: newAssignedNames,
    updatedAt: Timestamp.now(),
  });

  await assignmentService.logAction({
    workloadId,
    workloadName: existingData.name,
    projectId: existingData.projectId,
    action: 'assigned',
    description: `Assigned to ${userNames.join(', ')}`,
    performedBy,
    performedByName: performedByName || 'Admin',
    changes: [
      {
        field: 'assignedTo',
        previousValue: previousAssigned,
        newValue: newAssignedTo,
        displayNew: userNames.join(', '),
      },
    ],
  });
}

export async function unassignUsers(
  workloadId: string,
  userIds: string[],
  performedBy: string,
  userNames?: string[],
  performedByName?: string
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, workloadId);
  const existing = await getDoc(docRef);

  if (!existing.exists()) {
    throw new Error('Workload not found');
  }

  const existingData = existing.data() as Workload;
  const previousAssignedTo = existingData.assignedTo || [];
  const previousAssignedNames = existingData.assignedToNames || [];

  // Find the indices of users being removed to also remove their names
  const removedIndices = previousAssignedTo
    .map((id, idx) => (userIds.includes(id) ? idx : -1))
    .filter((idx) => idx !== -1);

  const newAssignedTo = previousAssignedTo.filter((id) => !userIds.includes(id));
  const newAssignedNames = previousAssignedNames.filter((_, idx) => !removedIndices.includes(idx));

  // Get names of removed users for logging
  const removedNames = userNames || removedIndices.map((idx) => previousAssignedNames[idx] || 'Unknown');

  await updateDoc(docRef, {
    assignedTo: newAssignedTo,
    assignedToNames: newAssignedNames,
    updatedAt: Timestamp.now(),
  });

  await assignmentService.logAction({
    workloadId,
    workloadName: existingData.name,
    projectId: existingData.projectId,
    action: 'unassigned',
    description: `Removed: ${removedNames.join(', ')}`,
    performedBy,
    performedByName: performedByName || 'Admin',
    changes: [
      {
        field: 'assignedTo',
        previousValue: previousAssignedTo,
        newValue: newAssignedTo,
        displayPrevious: removedNames.join(', '),
      },
    ],
  });
}

export async function assignToGroup(
  workloadId: string,
  groupId: string,
  groupName: string,
  performedBy: string
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, workloadId);
  const existing = await getDoc(docRef);

  if (!existing.exists()) {
    throw new Error('Workload not found');
  }

  const existingData = existing.data() as Workload;
  const previousGroups = existingData.assignedGroups || [];
  const newGroups = [...new Set([...previousGroups, groupId])];

  await updateDoc(docRef, {
    assignedGroups: newGroups,
    updatedAt: Timestamp.now(),
  });

  await assignmentService.logAction({
    workloadId,
    workloadName: existingData.name,
    projectId: existingData.projectId,
    action: 'assigned',
    description: `Assigned to group "${groupName}"`,
    changes: [
      {
        field: 'assignedGroups',
        previousValue: previousGroups,
        newValue: newGroups,
        displayNew: groupName,
      },
    ],
  });
}


export async function batchUpdateStatus(
  workloadIds: string[],
  status: WorkloadStatus,
  userId: string
): Promise<{ success: number; failed: number; errors: string[] }> {
  const batch = writeBatch(db);
  const errors: string[] = [];
  let successCount = 0;

  const batchId = `batch_${Date.now()}`;

  for (const workloadId of workloadIds) {
    try {
      const docRef = doc(db, COLLECTION_NAME, workloadId);
      batch.update(docRef, {
        status,
        updatedAt: Timestamp.now(),
      });
      successCount++;
    } catch (error: any) {
      errors.push(`${workloadId}: ${error.message}`);
    }
  }

  await batch.commit();

  return {
    success: successCount,
    failed: errors.length,
    errors,
  };
}

export async function batchDelete(
  workloadIds: string[],
  userId: string
): Promise<{ success: number; failed: number; errors: string[] }> {
  const batch = writeBatch(db);
  const errors: string[] = [];
  let successCount = 0;

  for (const workloadId of workloadIds) {
    try {
      const docRef = doc(db, COLLECTION_NAME, workloadId);
      batch.delete(docRef);
      successCount++;
    } catch (error: any) {
      errors.push(`${workloadId}: ${error.message}`);
    }
  }

  await batch.commit();

  return {
    success: successCount,
    failed: errors.length,
    errors,
  };
}


export async function deleteWorkload(workloadId: string, userId: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, workloadId);
  const existing = await getDoc(docRef);

  if (!existing.exists()) {
    throw new Error('Workload not found');
  }

  const existingData = existing.data() as Workload;

  // Note: This only deletes the workload document, not related data
  // Use cascadeDeleteWorkload for full deletion
  await deleteDoc(docRef);

  await assignmentService.logAction({
    workloadId,
    workloadName: existingData.name,
    projectId: existingData.projectId,
    action: 'deleted',
    description: `Workload "${existingData.name}" was deleted`,
    changes: [],
  });
}

/**
 * Cascade delete a workload and ALL related data:
 * - All addresses in the workload's address subcollection
 * - All survey responses linked to this workload
 * - The workload document itself
 *
 * This is a destructive operation and cannot be undone!
 */
export async function cascadeDeleteWorkload(
  workloadId: string,
  userId: string
): Promise<{ addressesDeleted: number; responsesDeleted: number }> {
  const docRef = doc(db, COLLECTION_NAME, workloadId);
  const existing = await getDoc(docRef);

  if (!existing.exists()) {
    throw new Error('Workload not found');
  }

  const existingData = existing.data() as Workload;
  let addressesDeleted = 0;
  let responsesDeleted = 0;

  // 1. Delete all addresses in the subcollection
  const addressesRef = collection(db, COLLECTION_NAME, workloadId, 'addresses');
  const addressSnapshot = await getDocs(addressesRef);

  // Delete in batches of 500 (Firestore limit)
  const addressBatches: typeof writeBatch[] = [];
  let currentBatch = writeBatch(db);
  let operationCount = 0;

  for (const addressDoc of addressSnapshot.docs) {
    currentBatch.delete(addressDoc.ref);
    operationCount++;
    addressesDeleted++;

    if (operationCount >= 500) {
      addressBatches.push(currentBatch as any);
      currentBatch = writeBatch(db);
      operationCount = 0;
    }
  }

  if (operationCount > 0) {
    addressBatches.push(currentBatch as any);
  }

  // Commit address deletion batches
  for (const batch of addressBatches) {
    await (batch as any).commit();
  }

  // 2. Delete all survey responses linked to this workload
  const responsesRef = collection(db, 'surveyResponses');
  const responsesQuery = query(responsesRef, where('workloadId', '==', workloadId));
  const responsesSnapshot = await getDocs(responsesQuery);

  const responseBatches: typeof writeBatch[] = [];
  currentBatch = writeBatch(db);
  operationCount = 0;

  for (const responseDoc of responsesSnapshot.docs) {
    currentBatch.delete(responseDoc.ref);
    operationCount++;
    responsesDeleted++;

    if (operationCount >= 500) {
      responseBatches.push(currentBatch as any);
      currentBatch = writeBatch(db);
      operationCount = 0;
    }
  }

  if (operationCount > 0) {
    responseBatches.push(currentBatch as any);
  }

  // Commit response deletion batches
  for (const batch of responseBatches) {
    await (batch as any).commit();
  }

  // 3. Delete the workload document itself
  await deleteDoc(docRef);

  // 4. Log the deletion
  await assignmentService.logAction({
    workloadId,
    workloadName: existingData.name,
    projectId: existingData.projectId,
    action: 'deleted',
    description: `Workload "${existingData.name}" permanently deleted with ${addressesDeleted} addresses and ${responsesDeleted} survey responses`,
    changes: [
      { field: 'addressesDeleted', previousValue: addressesDeleted, newValue: 0 },
      { field: 'responsesDeleted', previousValue: responsesDeleted, newValue: 0 },
    ],
  });

  return { addressesDeleted, responsesDeleted };
}

/**
 * Get deletion preview - shows what would be deleted without actually deleting
 */
export async function getWorkloadDeletionPreview(
  workloadId: string
): Promise<{ workloadName: string; addressCount: number; responseCount: number } | null> {
  const docRef = doc(db, COLLECTION_NAME, workloadId);
  const existing = await getDoc(docRef);

  if (!existing.exists()) {
    return null;
  }

  const existingData = existing.data() as Workload;

  const addressesRef = collection(db, COLLECTION_NAME, workloadId, 'addresses');
  const addressSnapshot = await getDocs(addressesRef);

  const responsesRef = collection(db, 'surveyResponses');
  const responsesQuery = query(responsesRef, where('workloadId', '==', workloadId));
  const responsesSnapshot = await getDocs(responsesQuery);

  return {
    workloadName: existingData.name,
    addressCount: addressSnapshot.size,
    responseCount: responsesSnapshot.size,
  };
}


export function subscribeToWorkload(
  workloadId: string,
  callback: (workload: Workload | null) => void
): () => void {
  const docRef = doc(db, COLLECTION_NAME, workloadId);

  return onSnapshot(docRef, (snapshot) => {
    if (snapshot.exists()) {
      callback({
        id: snapshot.id,
        ...snapshot.data(),
      } as Workload);
    } else {
      callback(null);
    }
  });
}

export function subscribeToProjectWorkloads(
  projectId: string,
  callback: (workloads: Workload[]) => void
): () => void {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('projectId', '==', projectId),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    const workloads = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Workload[];
    callback(workloads);
  });
}


export async function getWorkloadStats(projectId: string): Promise<{
  total: number;
  byStatus: Record<WorkloadStatus, number>;
  totalAddresses: number;
  completedAddresses: number;
  completionRate: number;
}> {
  const workloads = await getProjectWorkloads(projectId);

  const stats = {
    total: workloads.length,
    byStatus: {
      draft: 0,
      active: 0,
      paused: 0,
      completed: 0,
      archived: 0,
    } as Record<WorkloadStatus, number>,
    totalAddresses: 0,
    completedAddresses: 0,
    completionRate: 0,
  };

  workloads.forEach((workload) => {
    stats.byStatus[workload.status]++;
    stats.totalAddresses += workload.progress.totalAddresses;
    stats.completedAddresses += workload.progress.completed;
  });

  stats.completionRate =
    stats.totalAddresses > 0
      ? Math.round((stats.completedAddresses / stats.totalAddresses) * 100)
      : 0;

  return stats;
}

export const workloadService = {
  create: createWorkload,
  createFromSaturation,
  createFromCsv,
  createFromCensusBlocks,
  get: getWorkload,
  getProjectWorkloads,
  getUserWorkloads,
  getPaginated: getWorkloadsPaginated,
  update: updateWorkload,
  updateStatus: updateWorkloadStatus,
  updateProgress: updateWorkloadProgress,
  recalculateProgress,
  incrementSurveyResponse,
  linkSurvey,
  unlinkSurvey,
  assignToUsers,
  unassignUsers,
  assignToGroup,
  batchUpdateStatus,
  batchDelete,
  delete: deleteWorkload,
  cascadeDelete: cascadeDeleteWorkload,
  getDeletionPreview: getWorkloadDeletionPreview,
  subscribeToWorkload,
  subscribeToProjectWorkloads,
  getStats: getWorkloadStats,
};

export default workloadService;
